/* Custom Input Component to be used with Formik */
import React, { useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import {
  Text,
  FormErrorMessage,
  FormFieldInfo,
  FileInput,
  FormFieldLine,
  Spacer,
} from 'components/atoms';
import { modifiersType } from 'types'
import { applyStyleModifiers } from 'styled-components-modifiers';

// this spacing is used below the error message, this makes the margin feel more natural
// when the error happears and disappears
const InputSpacingFix = styled.div`
  height: ${props => props.theme.spacing.m};
  width: 100%;
`;

const FIELD_CUSTOM_MODIFIERS = {
  stretch: () => `
   width: 100%;
  `,
};


const FieldCustom = ({
  className,
  title,
  field, // { name, value, onChange, onBlur }
  form: {
    touched, errors, isValid, isSubmitting, setFieldTouched, setFieldValue
  }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  info,
  pre,
  shouldFocus,
  ...props
}) => {
  // focus on error after submit
  const fieldRef = useRef(null);
  useEffect(() => {
    if (isValid || !isSubmitting) return;
    const firstErrorKey = Object.keys(errors)[0];

    if (!isValid && firstErrorKey === field.name) {
      fieldRef.current.focus();
      setFieldTouched(field.name, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, isValid, errors]);

  // focus on mount
  useEffect(() => {
    if (shouldFocus && fieldRef && fieldRef.current) {
      fieldRef.current.focus();
    }
  }, [shouldFocus, fieldRef])

  const isError = useMemo(() => touched[field.name] && errors[field.name], [
    touched,
    errors,
    field.name,
  ]);

  const handleBlur = () => {
    if ('onBlur' in field && typeof field.onBlur === 'function') field.onBlur(field.name)
    if ('onBlur' in props && typeof props.onBlur === 'function') props.onBlur()
    setFieldTouched(field.name, true)
  }


  const handleChange = (file) => {
    setFieldValue(field.name, file);
  }

  return (
    <div className={className}>
      {title
      && (
        <Top>
          <Text modifiers={['base']}>{title}</Text>
        </Top>
      )
      }
      <FieldWrapper>
        {pre && (
          <FieldPre isError={isError} onClick={() => fieldRef.current.focus()}>
            {pre}
          </FieldPre>
        )}
        <FileInput
          onChange={handleChange}
          initialValue={field.value}
          onBlur={handleBlur}
          innerRef={fieldRef}
          {...props}
        />
      </FieldWrapper>
      <Spacer top="xs2" />
      <FormFieldLine />
      {isError && <FormErrorMessage>{errors[field.name]}</FormErrorMessage>}
      {info && <FormFieldInfo>{info}</FormFieldInfo>}
      <InputSpacingFix />
    </div>
  );
};

FieldCustom.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    onBlur: PropTypes.func
  }).isRequired,
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    setFieldTouched: PropTypes.func,
  }).isRequired,
  info: PropTypes.string,
  pre: PropTypes.string,
  onBlur: PropTypes.func,
  modifiers: modifiersType,
  shouldFocus: PropTypes.bool,
};

FieldCustom.defaultProps = {
  className: '',
  title: null,
  info: null,
  pre: null,
  onBlur: () => { },
  modifiers: [],
  shouldFocus: false,
};

const Top = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const FieldWrapper = styled.div`
  display: flex;
  cursor: pointer;
`

const FieldPre = styled.div`
  color: ${props => props.theme.colors.base};
  padding: ${props => props.theme.spacing.xs2};
  font-size: ${props => props.theme.sizes.s};
  font-weight: ${props => props.theme.weight.xbase};
  background-color: ${props => props.theme.colors.bg};
  border-bottom: 1px solid ${props => (props.isError ? props.theme.colors.danger : props.theme.colors.base)};
`

export default styled(FieldCustom)`
  ${Text} {
    padding-bottom: ${props => props.theme.spacing.s};
  }
  ${applyStyleModifiers(FIELD_CUSTOM_MODIFIERS)};
`;
